import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { formatDayLabel, formatTokens, TOKEN_TYPE_COLORS } from "./formatters";

type DayRow = {
  day: string;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  inputTokens: number;
  reasoningTokens: number;
};

const SERIES = [
  { key: "outputTokens", color: TOKEN_TYPE_COLORS.output.bar, name: "Output" },
  { key: "reasoningTokens", color: TOKEN_TYPE_COLORS.reasoning.bar, name: "Reasoning" },
  { key: "cacheReadTokens", color: TOKEN_TYPE_COLORS.cacheRead.bar, name: "Cache Read" },
  { key: "cacheWriteTokens", color: TOKEN_TYPE_COLORS.cacheWrite.bar, name: "Cache Write" },
  { key: "inputTokens", color: TOKEN_TYPE_COLORS.input.bar, name: "Base Input" },
] as const;

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string; name: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="border-cyber-border bg-cyber-surface text-cyber-text rounded border px-2 py-1.5 text-[10px] shadow-lg">
      <div className="mb-1 font-medium">{label}</div>
      {payload
        .filter((entry) => entry.value > 0)
        .map((entry) => (
          <div key={entry.dataKey} className="flex items-center gap-1.5">
            <div className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-cyber-muted">{entry.name}:</span>
            <span style={{ color: entry.color }}>{formatTokens(entry.value)}</span>
          </div>
        ))}
    </div>
  );
}

function CustomLegend({ payload }: { payload?: Array<{ value: string; color: string }> }) {
  if (!payload?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1">
      {payload.map((entry) => (
        <div key={entry.value} className="flex items-center gap-1.5 text-[11px]">
          <div
            className="h-2 w-2 flex-shrink-0 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-cyber-muted">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

export function DailyStackedChart({ data }: { data: DayRow[] }) {
  if (data.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -12 }}>
        <CartesianGrid
          vertical={false}
          stroke="rgb(var(--cyber-border))"
          strokeDasharray="3 3"
          strokeOpacity={0.5}
        />
        <XAxis
          dataKey="day"
          tickLine={false}
          axisLine={false}
          tick={{ fill: "rgb(var(--cyber-muted))", fontSize: 10 }}
          tickFormatter={formatDayLabel}
          tickMargin={8}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fill: "rgb(var(--cyber-muted))", fontSize: 10 }}
          tickFormatter={formatTokens}
          width={48}
        />
        <Tooltip
          content={<CustomTooltip />}
          cursor={{ fill: "rgb(var(--cyber-surface))", opacity: 0.5 }}
        />
        <Legend content={<CustomLegend />} />
        {SERIES.map((series) => (
          <Bar
            key={series.key}
            dataKey={series.key}
            name={series.name}
            stackId="tokens"
            fill={series.color}
            opacity={0.75}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
